import React, { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'umi';
import { Justify, Tabs, Radio, Table } from 'tea-component';

import RenderOperations from '@sg/components/Chart/Operations';
import Chart from '@sg/components/Chart';
import TableContent from '@sg/components/TableContent';
import Loading from '@intelligence/components/Loading';
import CommonBubble from '@home/components/CommonBubble';

import { pickOperations } from '@sg/commonData/pickOperations';
import { xAxisHighLight, applyValueAxisBoundaryGap } from '@sg/utils/chatUtils';
import { dealTypeValue, mergeChartDataLabelStyle } from '@sg/utils/useChartOptions';
import { financeFormat } from '@sg/utils/common';
import { appendStackedTotalLabelSeries } from '@/pages/strategic/utils/stackedTotalLabel';
import { buildGroupedChartTooltipFormatter } from '@/pages/strategic/utils/buildGroupedChartTooltip';
import { createBtnPoint, SGBuriedBtnPoint } from '@/pages/strategic/utils/buriedPoint';

import {
  HEADCOUNT_CATEGORY_LEVEL_OPTIONS,
  HEADCOUNT_CHART_VALUE_TYPE,
  buildHeadcountTooltipSeries,
  type THeadcountCategoryLevel,
  type THeadcountEmploymentType,
} from './config/headcountCategoryConfig';
import {
  getHeadcountTableColumns,
  getHeadcountTableHeaderGroups,
  getHeadcountTableMinWidth,
} from './config/headcountTableConfig';
import { getStaffRateColumns } from './config/staffRateColumns';
import {
  DefaultFinancialsChat,
  getStudioCardChartHeight,
  getStudioCardTableBodyMaxHeight,
  showFinancialsList,
  getTabs,
} from '../../Studios/config';
import { useStaffRateData } from './hooks/useStaffRateData';
import { useHeadRateData } from './hooks/useHeadRateData';
import { applyWrappedXAxisLabels } from '../utils/applyTruncatedXAxisLabels';
import './index.scss';

type TViewStateKey = 'financials' | 'burnrate' | 'headcount' | 'staffrate';
// Studios: radios below tabs (unchanged). Portfolio: radios trail beside eye/chart icons.
type TCategoryRadioPlacement = 'below-header' | 'header-trailing';

type IHeadcountCardProps = {
  activeDateTypeSegment: string;
  selectedMonth?: string | null;
  smallChartViewState: Record<string, boolean>;
  updateStudioEyesViewState: (viewKey: TViewStateKey, enabled: boolean) => void;
  studioInfo?: string;
  buriedPointTab: any;
  studioName?: string;
} & (
  | { scope: 'studio'; studioId: string }
  | { scope: 'portfolio' }
);

const HeadcountCard: React.FC<IHeadcountCardProps> = (props) => {
  const {
    activeDateTypeSegment,
    selectedMonth,
    smallChartViewState,
    updateStudioEyesViewState,
    studioInfo = '',
    buriedPointTab,
  } = props;
  const scope = props.scope;
  const scopeOptions = props.scope === 'studio'
    ? { scope: 'studio' as const, studioId: props.studioId }
    : { scope: 'portfolio' as const };
  const { formatMessage } = useIntl();

  const [showHeadRateChatsType, setShowHeadRateChatsType] = useState<any>(DefaultFinancialsChat);
  const [headcountActive, setHeadcountActive] = useState('headcount');
  const [headcountCategoryLevel, setHeadcountCategoryLevel] =
    useState<THeadcountCategoryLevel>('level_1');

  const tabs = useMemo(() => {
    const baseTabs = getTabs(formatMessage);
    return baseTabs.map((tab) => {
      if (tab.id !== 'headcount') {
        return tab;
      }
      return {
        ...tab,
        label: (
          <CommonBubble
            // style={{ maxWidth: '360px' }}
            placement={scope === 'portfolio' ? 'top-start' : 'top'}
            arrowPointAtCenter
            content={formatMessage({ id: 'dbs_studios_headcount_tab_tip' })}
          >
            {tab.label}
          </CommonBubble>
        ),
      };
    });
  }, [formatMessage]);

  const staffRateColumns = useMemo(
    () => getStaffRateColumns(scope),
    [scope],
  );

  // The same operation toolbar drives both tabs, so resolve its persisted view
  // state from the currently active tab before syncing the local toolbar state.
  const activeHeadRateViewKey = headcountActive === 'staffrate' ? 'staffrate' : 'headcount';
  const activeHeadRateViewEnabled = !!smallChartViewState[activeHeadRateViewKey];

  useEffect(() => {
    setShowHeadRateChatsType((prev: any) => ({
      ...prev,
      format: activeHeadRateViewEnabled,
    }));
  }, [activeHeadRateViewEnabled]);

  const {
    headRateData,
    headRateTableData,
    headcountUpdateTime,
    headRateLoading,
    hasHeadcountData,
    hasLiveopsData,
  } = useHeadRateData(
    scopeOptions,
    activeDateTypeSegment,
    showHeadRateChatsType,
    selectedMonth,
    headcountCategoryLevel,
    formatMessage,
  );

  const {
    staffRateData,
    staffRateTableData,
    staffRateUpdateTime,
    staffRateLoading,
  } = useStaffRateData(
    scopeOptions,
    activeDateTypeSegment,
    formatMessage,
    selectedMonth,
  );

  const headcountTableColumns = useMemo(
    () => getHeadcountTableColumns(
      headcountCategoryLevel,
      formatMessage,
      scope,
      hasLiveopsData,
    ),
    [headcountCategoryLevel, formatMessage, scope, hasLiveopsData],
  );

  const activeColumns = headcountActive === 'staffrate'
    ? staffRateColumns
    : headcountTableColumns;

  const tableHeaderGroups = useMemo(() => {
    if (headcountActive !== 'headcount') {
      return null;
    }
    return getHeadcountTableHeaderGroups(
      headcountCategoryLevel,
      formatMessage,
      hasLiveopsData,
    );
  }, [headcountActive, headcountCategoryLevel, formatMessage, hasLiveopsData]);

  const activeLoading = headcountActive === 'staffrate' ? staffRateLoading : headRateLoading;
  const activeChartData = headcountActive === 'staffrate' ? staffRateData : headRateData;
  const activeTableData = headcountActive === 'staffrate' ? staffRateTableData : headRateTableData;
  const activeUpdateTime = headcountActive === 'staffrate'
    ? staffRateUpdateTime
    : headcountUpdateTime;

  const showHeadcountCategoryRadio = headcountActive === 'headcount' && hasHeadcountData && activeDateTypeSegment === 'month';

  // Reversed from the first draft: only Portfolio trails radios in the header ops row.
  const categoryRadioPlacement: TCategoryRadioPlacement =
    scope === 'portfolio' ? 'header-trailing' : 'below-header';
  // Below-header row (Studios) shrinks chart/table so it still aligns with Financials.
  const hasBelowHeaderCategoryRadio =
    showHeadcountCategoryRadio && categoryRadioPlacement === 'below-header';

  const headcountChartHeight = useMemo(
    () => getStudioCardChartHeight({ hasHeadcountCategoryRadio: hasBelowHeaderCategoryRadio }),
    [hasBelowHeaderCategoryRadio],
  );

  const tableBodyMaxHeight = useMemo(
    () => getStudioCardTableBodyMaxHeight({
      hasHeadcountCategoryRadio: hasBelowHeaderCategoryRadio,
      tableHeaderRows: tableHeaderGroups?.length ? 2 : 1,
    }),
    [hasBelowHeaderCategoryRadio, tableHeaderGroups],
  );

  const tableAddons = useMemo(() => {
    const { groupable, scrollable } = Table.addons;
    const addons = [
      // scrollable must stay first so minWidth horizontal scroll works with groupable.
      scrollable({
        minWidth: headcountActive === 'headcount'
          ? getHeadcountTableMinWidth(headcountCategoryLevel, scope, hasLiveopsData)
          : 'auto',
        maxHeight: tableBodyMaxHeight,
      }),
    ];

    if (tableHeaderGroups?.length) {
      addons.push(groupable({ headerGroups: tableHeaderGroups }));
    }

    return addons;
  }, [headcountActive, headcountCategoryLevel, tableHeaderGroups, tableBodyMaxHeight, scope, hasLiveopsData]);

  // Handle headcount/staff rate chart type change with buried point tracking
  const handleShowHeadRateChatsTypeChange = (e: any) => {
    // Determine section type based on active tab (Headcount or Staff Rate)
    const sectionType = headcountActive === 'headcount' ? 'Headcount' : 'Staff Rate';
    const viewKey = headcountActive === 'headcount' ? 'headcount' : 'staffrate';
    const currentViewEnabled = !!smallChartViewState[viewKey];

    if (e?.value) {
      const value = e.value;
      const isViewClick = value === 'view'
        || (e.format !== showHeadRateChatsType?.format && value === showHeadRateChatsType?.value);

      if (isViewClick) {
        const nextViewEnabled = !currentViewEnabled;
        const buttonKey = headcountActive === 'headcount'
          ? 'headcountView'
          : 'staffRateView';
        const btnPoint = createBtnPoint(
          buriedPointTab,
          buttonKey,
          `${sectionType} - View - ${nextViewEnabled ? 'On' : 'Off'} - ${studioInfo}`,
        );
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
        updateStudioEyesViewState(viewKey, nextViewEnabled);
        setShowHeadRateChatsType((prev: any) => ({
          ...prev,
          format: nextViewEnabled,
        }));
        return;
      }

      if (value === 'bar') {
        // Only track if switching to bar (not already on bar)
        if (showHeadRateChatsType?.value !== 'bar') {
          const buttonKey = headcountActive === 'headcount' ? 'headcountBarChart' : 'staffRateBarChart';
          const btnPoint = createBtnPoint(
            buriedPointTab,
            buttonKey,
            `${sectionType} - Bar Chart - ${studioInfo}`
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
      } else if (value === 'table') {
        // Only track if switching to table (not already on table)
        if (showHeadRateChatsType?.value !== 'table') {
          const buttonKey = headcountActive === 'headcount' ? 'headcountTable' : 'staffRateTable';
          const btnPoint = createBtnPoint(
            buriedPointTab,
            buttonKey,
            `${sectionType} - Table - ${studioInfo}`
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
      }
    }

    // Call the original setter
    setShowHeadRateChatsType({
      ...e,
      format: currentViewEnabled,
    });
  };

  const categoryRadioGroup = showHeadcountCategoryRadio ? (
    <Radio.Group
      className={`dbs-headcount-category-radio-group dbs-headcount-category-radio-group--${categoryRadioPlacement}`}
      value={headcountCategoryLevel}
      onChange={(value: string) => {
        if (value !== headcountCategoryLevel) {
          const option = HEADCOUNT_CATEGORY_LEVEL_OPTIONS.find(
            (item) => item.value === value,
          );
          const label = option
            ? formatMessage({ id: option.labelKey })
            : value;
          const btnPoint = createBtnPoint(
            buriedPointTab,
            'headcountCategoryLevel',
            `Headcount - Category Level - ${label} - ${studioInfo}`,
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
        setHeadcountCategoryLevel(value as THeadcountCategoryLevel);
      }}
    >
      {HEADCOUNT_CATEGORY_LEVEL_OPTIONS.map((option) => (
        <Radio
          key={option.value}
          // className="dbg-common-radio"
          name={option.value}
        >
          <CommonBubble
            style={{ maxWidth: '450px' }}
            placement="top"
            content={formatMessage({ id: option.tipKey })}
          >
            {formatMessage({ id: option.labelKey })}
          </CommonBubble>
        </Radio>
      ))}
    </Radio.Group>
  ) : null;

  return (
    <div className={`dbs-by-date-card dbs-headcount-card dbs-headcount-card--${scope}`}>
      <Justify
        bottom
        left={
          <div className="dbo-card-title dbs-by-date-card-title">
            <Tabs
              tabs={tabs}
              placement={"top"}
              activeId={headcountActive}
              onActive={(e) => {
                // Track buried point for tab change
                if (e.id === 'headcount') {
                  const btnPoint = createBtnPoint(
                    buriedPointTab,
                    'headcount',
                    `Headcount - ${studioInfo}`
                  );
                  if (btnPoint) {
                    SGBuriedBtnPoint(btnPoint);
                  }
                } else if (e.id === 'staffrate') {
                  const btnPoint = createBtnPoint(
                    buriedPointTab,
                    'staffRate',
                    `Staff Rate - ${studioInfo}`
                  );
                  if (btnPoint) {
                    SGBuriedBtnPoint(btnPoint);
                  }
                }

                setHeadcountActive(e.id);
              }}
            />
            <div className='dbo-card-title-sub'>
              {activeUpdateTime
                ? `${formatMessage({ id: 'dbs_update_on' })} ${activeUpdateTime}`
                : ''}
            </div>
          </div>
        }
        right={
          <div className="dbs-headcount-card__header-ops">
            {categoryRadioPlacement === 'header-trailing' && categoryRadioGroup}
            {/* Keep slot icons in their own group so radio ↔ ops gap is not per-icon. */}
            <div className="dbs-headcount-card__ops-slots">
              <RenderOperations
                key={`headrate-ops-${headcountActive}-${showHeadRateChatsType.value}-${activeHeadRateViewEnabled}`}
                defaultValue={showHeadRateChatsType.value}
                defaultValueShow={showHeadRateChatsType.format}
                change={handleShowHeadRateChatsTypeChange}
                options={pickOperations(['view', ...showFinancialsList], formatMessage)}
              />
            </div>
          </div>
        }
      />

      {categoryRadioPlacement === 'below-header' && categoryRadioGroup}

      {activeLoading && (
        <Loading style={{ height: headcountChartHeight }} />
      )}

      {!activeLoading && showHeadRateChatsType?.value !== 'table' && activeChartData[showHeadRateChatsType?.value] && (() => {

        return (
          <Chart
            data={activeChartData[showHeadRateChatsType?.value]}
            key={`${headcountActive}HeadRate${showHeadRateChatsType?.value}-${headcountCategoryLevel}`}
            type={showHeadRateChatsType?.value}
            height={headcountChartHeight}
            formatType="bmk"
            numberType={
              headcountActive === 'headcount' && showHeadRateChatsType?.value === 'bar'
                ? HEADCOUNT_CHART_VALUE_TYPE
                : ''
            }
            setOtherConfigFunc={(opt: any) => {
              opt.grid.containLabel = true;
              if (showHeadRateChatsType?.value === 'bar') {
                // Pad y-axis so bar/total labels are not clipped at the chart edge.
                applyValueAxisBoundaryGap(opt);
              }

              if (
                headcountActive === 'headcount'
                && showHeadRateChatsType?.value === 'bar'
              ) {
                // Group FTE and temporary rows in the tooltip to match the
                // two-stack visual structure used by the headcount bar chart.
                opt.tooltip = {
                  ...(opt.tooltip || {}),
                  formatter: buildGroupedChartTooltipFormatter<THeadcountEmploymentType>({
                    series: buildHeadcountTooltipSeries(
                      headcountCategoryLevel,
                      (labelKey) => formatMessage({ id: labelKey }),
                      hasLiveopsData,
                    ),
                    groupOrder: ['fte', 'temporary'],
                    groupTotalTitles: {
                      fte: formatMessage({
                        id: 'dbs_studios_headcount_fte_total',
                      }),
                      temporary: formatMessage({
                        id: 'dbs_studios_headcount_temporary_total',
                      }),
                    },
                    valueType: HEADCOUNT_CHART_VALUE_TYPE,
                  }),
                };
              }

              opt = xAxisHighLight(opt, {
                type: showHeadRateChatsType?.value === 'bar' ? 'shadow' : 'line',
                lineStyle: {
                  color: '#BBBDCB',
                  width: 1,
                  type: 'solid',
                },
              } as any);
              // After xAxisHighLight: truncate ticks only; hover keeps full name.
              if (scope === 'portfolio') {
                applyWrappedXAxisLabels(opt);
              }

              if (
                headcountActive === 'headcount'
                && showHeadRateChatsType?.value === 'bar'
              ) {
                // Add one synthetic total-label series per stack so each
                // employment group can show its own total independently.
                opt = appendStackedTotalLabelSeries(opt, {
                  enabled: activeHeadRateViewEnabled,
                  seriesName: '__fte_total_label__',
                  stackName: 'FTE',
                  labelFormatter: (value: number) => (
                    dealTypeValue(HEADCOUNT_CHART_VALUE_TYPE, value)
                  ),
                });
                opt = appendStackedTotalLabelSeries(opt, {
                  enabled: activeHeadRateViewEnabled,
                  seriesName: '__temporary_total_label__',
                  stackName: 'Temporary',
                  labelFormatter: (value: number) => (
                    dealTypeValue(HEADCOUNT_CHART_VALUE_TYPE, value)
                  ),
                });
                return opt;
              }

              if (
                headcountActive === 'staffrate'
                && showHeadRateChatsType?.value === 'bar'
                && activeHeadRateViewEnabled
                && Array.isArray(opt?.series)
              ) {
                opt.series = opt.series.map((series: any) => {
                  if (series?.type !== 'bar') {
                    return series;
                  }
                  return {
                    ...series,
                    label: mergeChartDataLabelStyle({
                      ...(series?.label || {}),
                      show: activeHeadRateViewEnabled,
                      distance: 8,
                      formatter: (params: any) => {
                        const value = params?.value?.value ?? params?.value;
                        if (value === null || value === undefined) return '';
                        return financeFormat(value, 1);
                      },
                    }),
                  };
                });
              }

              return opt;
            }}
          />
        );
      })()}

      {!activeLoading && showHeadRateChatsType?.value === 'table' && (
        <TableContent
          key={`headcount-table-${headcountActive}-${headcountCategoryLevel}`}
          className={
            headcountActive === 'headcount' && headcountCategoryLevel !== 'level_1'
              ? 'dbs-commons-table dbs-studios-headcount-table'
              : 'dbs-commons-table dbs-chats-table'
          }
          emptyMinHeight={headcountChartHeight}
          datas={activeTableData}
          columns={activeColumns}
          sort={[{ by: 'fill_time', order: 'desc' }]}
          resultKey="list"
          recordKey="key"
          addons={tableAddons}
        />
      )}
    </div>
  );
};

export default HeadcountCard;
